import React, { useState, useEffect, useCallback } from "react";
import style from "./style.module.css";
import TruncatedText from "../../Components/TruncatedText";
import { usePermissions } from "../../hooks/usePermissions";
import { useCookies } from "react-cookie";
import { authenticatedFetch } from "../../utils/api";
import useSnackbar from "../../context/Snackbar/useSnackbar";
import { API_BASE } from "../../config/api";
import { formatCurrency } from "../../config/currency";
import Pagination from "../../Components/Pagination";

const PAGE_SIZE = 25;

interface Coupon {
  coupon_id: number;
  coupon_name: string;
  coupon_type: string;
  coupon_value: string;
  valid_from: string;
  valid_until: string;
  user_id: number;
  user_name?: string;
  user_email?: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

interface User {
  user_id: number;
  user_first_name: string;
  user_last_name: string;
  email: string;
  user_type: string;
}

interface EditCoupon {
  coupon_id?: number;
  coupon_name: string;
  coupon_type: string;
  coupon_value: string;
  valid_from: string;
  valid_until: string;
  is_active: boolean;
}

const CouponsPage = () => {
  const [coupons, setCoupons] = useState<Coupon[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [selectedCoupons, setSelectedCoupons] = useState<number[]>([]);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [editCoupon, setEditCoupon] = useState<EditCoupon>({
    coupon_name: "",
    coupon_type: "percentage",
    coupon_value: "",
    valid_from: "",
    valid_until: "",
    is_active: true
  });
  const [customCouponType, setCustomCouponType] = useState("");
  const [showCustomInput, setShowCustomInput] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const permissions = usePermissions();
  const [cookies] = useCookies();
  const snack = useSnackbar();

  const fetchCoupons = useCallback(async () => {
    try {
      const params = new URLSearchParams({ page: String(page), limit: String(PAGE_SIZE) });
      if (debouncedSearchTerm) params.set('search', debouncedSearchTerm);
      const response = await authenticatedFetch(`${API_BASE}/coupon?${params}`, {
        method: 'GET'
      }, cookies.auth);

      if (response.ok) {
        const result = await response.json();
        setCoupons(result.data || []);
        setTotalPages(result.totalPages || 1);
        setTotalItems(result.total || 0);
      } else {
        console.error("Failed to fetch coupons. Status:", response.status);
      }
    } catch (error) {
      console.error("Error fetching coupons:", error);
    }
  }, [cookies.auth, page, debouncedSearchTerm]);

  const fetchUsers = useCallback(async () => {
    try {
      // Lookup only (resolve user_id -> name) — request the max page size
      // rather than paginating a lookup.
      const response = await authenticatedFetch(`${API_BASE}/user/users?page=1&limit=100`, {
        method: 'GET'
      }, cookies.auth);

      if (response.ok) {
        const data = await response.json();
        setUsers(data?.users || []);
      } else {
        console.error("Failed to fetch users. Status:", response.status);
        setUsers([]);
      }
    } catch (error) {
      console.error("Error fetching users:", error);
      setUsers([]);
    }
  }, [cookies.auth]);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearchTerm(searchTerm), 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => { setPage(1); }, [debouncedSearchTerm]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  useEffect(() => { fetchCoupons(); }, [fetchCoupons]);

  const handleAdd = () => {
    setEditCoupon({
      coupon_name: "",
      coupon_type: "percentage",
      coupon_value: "",
      valid_from: "",
      valid_until: "",
      is_active: true
    });
    setCustomCouponType("");
    setShowCustomInput(false);
    setShowAddModal(true);
  };

  const handleCouponTypeChange = (value: string) => {
    if (value === "custom") {
      setShowCustomInput(true);
      setEditCoupon({...editCoupon, coupon_type: customCouponType || ""});
    } else {
      setShowCustomInput(false);
      setEditCoupon({...editCoupon, coupon_type: value});
    }
  };

  const handleCustomCouponTypeChange = (value: string) => {
    setCustomCouponType(value);
    setEditCoupon({...editCoupon, coupon_type: value});
  };

  const handleUpdate = () => {
    if (selectedCoupons.length !== 1) {
      alert("Please select exactly one coupon to update");
      return;
    }
    
    const couponToEdit = coupons.find(coupon => coupon.coupon_id === selectedCoupons[0]);
    if (couponToEdit) {
      // Check if the coupon type is in the predefined list
      const predefinedTypes = [
        "percentage", "fixed", "buy_x_get_y", "free_shipping", "cashback",
        "loyalty", "seasonal", "first_time", "bulk", "referral"
      ];
      const isCustomType = !predefinedTypes.includes(couponToEdit.coupon_type);
      
      setEditCoupon({
        coupon_id: couponToEdit.coupon_id,
        coupon_name: couponToEdit.coupon_name,
        coupon_type: isCustomType ? "custom" : couponToEdit.coupon_type,
        coupon_value: couponToEdit.coupon_value,
        valid_from: couponToEdit.valid_from.split('T')[0], // Format for date input
        valid_until: couponToEdit.valid_until.split('T')[0],
        is_active: couponToEdit.is_active
      });
      
      // Set custom type state if it's a custom type
      if (isCustomType) {
        setCustomCouponType(couponToEdit.coupon_type);
        setShowCustomInput(true);
      } else {
        setCustomCouponType("");
        setShowCustomInput(false);
      }
      
      setShowEditModal(true);
    }
  };

  const handleDelete = () => {
    if (selectedCoupons.length === 0) {
      alert("Please select at least one coupon to delete");
      return;
    }
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    try {
      setIsLoading(true);
      for (const couponId of selectedCoupons) {
        const response = await authenticatedFetch(`${API_BASE}/coupon/${couponId}`, {
          method: "DELETE"
        }, cookies.auth);
        
        if (!response.ok) {
          console.error(`Failed to delete coupon ${couponId}. Status:`, response.status);
        }
      }
      setSelectedCoupons([]);
      setShowDeleteConfirm(false);
      await fetchCoupons();
      snack.onResponse({
        message: "Coupon(s) deleted successfully!",
        status: 200,
      });
    } catch (error) {
      console.error("Error deleting coupons:", error);
      snack.onResponse({
        message: "Error deleting coupon(s)",
        status: 500,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async (isEdit: boolean) => {
    try {
      setIsLoading(true);

      // Validate required fields
      if (!editCoupon.coupon_name.trim()) {
        alert("Coupon name is required");
        return;
      }

      if (!editCoupon.valid_from) {
        alert("Valid from date is required");
        return;
      }

      if (!editCoupon.valid_until) {
        alert("Valid until date is required");
        return;
      }

      // For numeric types, validate as numbers
      const numericTypes = ["percentage", "fixed", "buy_x_get_y", "cashback", "loyalty", "seasonal", "first_time", "bulk", "referral"];
      
      if (numericTypes.includes(editCoupon.coupon_type)) {
        const couponValue = parseFloat(editCoupon.coupon_value);
        if (isNaN(couponValue) || couponValue <= 0) {
          alert("Coupon value must be a positive number for this type");
          return;
        }

        if (editCoupon.coupon_type === "percentage" && couponValue > 100) {
          alert("Percentage discount cannot exceed 100%");
          return;
        }
      }
      
      // For non-numeric types (free_shipping, custom), allow any text value

      // Validate date range
      const validFromDate = new Date(editCoupon.valid_from);
      const validUntilDate = new Date(editCoupon.valid_until);

      if (validUntilDate < validFromDate) {
        alert("Valid until date must be after valid from date");
        return;
      }

      // Determine the value to send to backend
      let backendValue;
      if (numericTypes.includes(editCoupon.coupon_type)) {
        backendValue = parseFloat(editCoupon.coupon_value);
      } else {
        // For text-based types, send as string
        backendValue = editCoupon.coupon_value.trim();
      }

      const payload = {
        coupon_name: editCoupon.coupon_name.trim(),
        coupon_type: editCoupon.coupon_type,
        coupon_value: backendValue,
        valid_from: new Date(editCoupon.valid_from).toISOString(),
        valid_until: new Date(editCoupon.valid_until).toISOString(),
        user_id: cookies.auth?.user_id || 1,
        is_active: editCoupon.is_active
      };

      const url = isEdit 
        ? `${API_BASE}/coupon/${editCoupon.coupon_id}`
        : `${API_BASE}/coupon`;
      
      const method = isEdit ? "PUT" : "POST";

      const response = await authenticatedFetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload)
      }, cookies.auth);

      if (response.ok) {
        setShowEditModal(false);
        setShowAddModal(false);
        setEditCoupon({
          coupon_name: "",
          coupon_type: "percentage",
          coupon_value: "",
          valid_from: "",
          valid_until: "",
          is_active: true
        });
        setSelectedCoupons([]);
        // Refresh both coupons and users to ensure user info is available
        await Promise.all([fetchCoupons(), fetchUsers()]);
        snack.onResponse({
          message: "Coupon saved successfully!",
          status: 200,
        });
      } else {
        const errorText = await response.text();
        console.error("Failed to save coupon. Status:", response.status, "Error:", errorText);
        snack.onResponse({
          message: `Failed to save coupon: ${errorText}`,
          status: response.status,
        });
      }
    } catch (error) {
      console.error("Error saving coupon:", error);
      snack.onResponse({
        message: `Error saving coupon: ${error instanceof Error ? error.message : String(error)}`,
        status: 500,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedCoupons(coupons.map(coupon => coupon.coupon_id));
    } else {
      setSelectedCoupons([]);
    }
  };

  const handleSelectCoupon = (couponId: number, checked: boolean) => {
    if (checked) {
      setSelectedCoupons([couponId]);
    } else {
      setSelectedCoupons(selectedCoupons.filter(id => id !== couponId));
    }
  };

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return "N/A";
    try {
      return new Date(dateString).toLocaleDateString();
    } catch {
      return "Invalid Date";
    }
  };

  const formatCouponValue = (value: number | string, type: string) => {
    // Convert value to number if it's a string
    const numValue = typeof value === 'string' ? parseFloat(value) : value;
    
    // Handle NaN case
    if (isNaN(numValue)) {
      return value.toString();
    }
    
    switch (type) {
      case "percentage":
        return `${numValue}%`;
      case "fixed":
        return formatCurrency(numValue);
      case "buy_x_get_y":
        return `Buy ${Math.floor(numValue)} Get 1`;
      case "free_shipping":
        return "Free";
      case "cashback":
        return formatCurrency(numValue);
      case "loyalty":
        return `${numValue} points`;
      case "seasonal":
        return `${numValue}% off`;
      case "first_time":
        return `${numValue}% off`;
      case "bulk":
        return `${numValue}% off`;
      case "referral":
        return formatCurrency(numValue);
      default:
        // For custom types, just return the value as is
        return value.toString();
    }
  };

  const getUserName = useCallback((userId: number | null | undefined): string => {
    if (!userId) {
      return "Unknown";
    }
    
    // Ensure users is an array before calling find
    if (!Array.isArray(users) || users.length === 0) {
      return "Loading...";
    }
    
    // Try to find user by matching user_id (handle both number and string comparisons)
    const user = users.find(u => {
      const uId = Number(u.user_id);
      const cId = Number(userId);
      return uId === cId && !isNaN(uId) && !isNaN(cId);
    });
    
    if (user) {
      const firstName = user.user_first_name || '';
      const lastName = user.user_last_name || '';
      const fullName = `${firstName} ${lastName}`.trim();
      if (fullName) {
        return fullName;
      }
    }
    
    // If user not found, return the user ID for now
    return `User ${userId}`;
  }, [users]);

  return (
    <div className={style.wrapper}>
      {/* Header Section */}
      <div className={style.header}>
        <h1 className={style.title}>Coupons</h1>
        <div className={style.searchSection}>
          <input
            type="text"
            placeholder="Search by coupon name..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className={style.searchInput}
          />
          {permissions.canManageCoupons && (
            <div className={style.actionButtons}>
              <button className={style.actionBtn} onClick={handleAdd}>
                Add Coupon
              </button>
              <button className={style.actionBtn} onClick={handleUpdate}>
                Update Coupon
              </button>
              <button className={style.actionBtnDanger} onClick={handleDelete}>
                Delete Coupon
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Table Section */}
      <div className={style.tableContainer}>
        <table className={style.dataTable}>
          <thead>
            <tr>
              {permissions.canManageCoupons && (
                <th>
                  <input
                    type="checkbox"
                    checked={selectedCoupons.length === coupons.length && coupons.length > 0}
                    onChange={(e) => handleSelectAll(e.target.checked)}
                    className={style.checkbox}
                  />
                </th>
              )}
              <th>Coupon ID</th>
              <th>Coupon Name</th>
              <th>Type</th>
              <th>Value</th>
              <th>Valid From</th>
              <th>Valid Until</th>
              <th>User</th>
              <th>Active</th>
              <th>Created At</th>
              <th>Updated At</th>
            </tr>
          </thead>
          <tbody>
            {coupons.map((coupon) => (
              <tr key={coupon.coupon_id} className={style.tableRow}>
                {permissions.canManageCoupons && (
                  <td>
                    <input
                      type="checkbox"
                      checked={selectedCoupons.includes(coupon.coupon_id)}
                      onChange={(e) => handleSelectCoupon(coupon.coupon_id, e.target.checked)}
                      className={style.checkbox}
                    />
                  </td>
                )}
                <td>{coupon.coupon_id}</td>
                <td>{coupon.coupon_name}</td>
                <td>
                  <span className={`${style.couponType} ${style[coupon.coupon_type]}`}>
                    {coupon.coupon_type}
                  </span>
                </td>
                <td>{formatCouponValue(coupon.coupon_value, coupon.coupon_type)}</td>
                <td>{formatDate(coupon.valid_from)}</td>
                <td>{formatDate(coupon.valid_until)}</td>
                <td>
                  {coupon.user_name || (coupon.user_id ? getUserName(coupon.user_id) : "Unknown")}
                </td>
                <td>{coupon.is_active ? 'Yes' : 'No'}</td>
                <td>{formatDate(coupon.created_at)}</td>
                <td>{formatDate(coupon.updated_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Pagination page={page} totalPages={totalPages} onPageChange={setPage} totalItems={totalItems} />

      {/* Add Modal */}
      {showAddModal && (
        <div className={style.modalOverlay}>
          <div className={style.modal}>
            <h2>Add Coupon</h2>
            <div className={style.formGroup}>
              <label>Coupon Name:</label>
              <input
                type="text"
                value={editCoupon.coupon_name}
                onChange={(e) => setEditCoupon({...editCoupon, coupon_name: e.target.value})}
                className={style.modalInput}
                placeholder="Enter coupon name *"
                required
              />
            </div>
            <div className={style.formGroup}>
              <label>Coupon Type:</label>
              <select
                value={editCoupon.coupon_type === customCouponType ? "custom" : editCoupon.coupon_type}
                onChange={(e) => handleCouponTypeChange(e.target.value)}
                className={style.modalInput}
                required
              >
                <option value="percentage">Percentage Discount</option>
                <option value="fixed">Fixed Amount Discount</option>
                <option value="buy_x_get_y">Buy X Get Y</option>
                <option value="free_shipping">Free Shipping</option>
                <option value="cashback">Cashback</option>
                <option value="loyalty">Loyalty Points</option>
                <option value="seasonal">Seasonal</option>
                <option value="first_time">First Time Customer</option>
                <option value="bulk">Bulk Purchase</option>
                <option value="referral">Referral Bonus</option>
                <option value="custom">Custom</option>
              </select>
              {showCustomInput && (
                <input
                  type="text"
                  value={customCouponType}
                  onChange={(e) => handleCustomCouponTypeChange(e.target.value)}
                  placeholder="Enter custom coupon type"
                  className={style.modalInput}
                  style={{ marginTop: '5px' }}
                />
              )}
            </div>
            <div className={style.formGroup}>
              <label>Coupon Value:</label>
              <input
                type="text"
                value={editCoupon.coupon_value}
                onChange={(e) => setEditCoupon({...editCoupon, coupon_value: e.target.value})}
                className={style.modalInput}
                placeholder={
                  editCoupon.coupon_type === "percentage" ? "Enter percentage (0-100) *" :
                  editCoupon.coupon_type === "fixed" ? "Enter amount *" :
                  editCoupon.coupon_type === "buy_x_get_y" ? "Enter quantity to buy *" :
                  editCoupon.coupon_type === "free_shipping" ? "Enter minimum order amount or 'Free' *" :
                  editCoupon.coupon_type === "cashback" ? "Enter cashback amount *" :
                  editCoupon.coupon_type === "loyalty" ? "Enter points value *" :
                  editCoupon.coupon_type === "seasonal" ? "Enter discount percentage *" :
                  editCoupon.coupon_type === "first_time" ? "Enter discount percentage *" :
                  editCoupon.coupon_type === "bulk" ? "Enter discount percentage *" :
                  editCoupon.coupon_type === "referral" ? "Enter bonus amount *" :
                  editCoupon.coupon_type === "custom" ? "Enter any value (text, number, symbol) *" :
                  "Enter coupon value *"
                }         
                required
              />
            </div>
            <div className={style.formGroup}>
              <label>Valid From:</label>
              <input
                type="date"
                value={editCoupon.valid_from}
                onChange={(e) => setEditCoupon({...editCoupon, valid_from: e.target.value})}
                className={style.modalInput}
                min={new Date().toISOString().split('T')[0]}
                required
              />
            </div>
            <div className={style.formGroup}>
              <label>Valid Until:</label>
              <input
                type="date"
                value={editCoupon.valid_until}
                onChange={(e) => setEditCoupon({...editCoupon, valid_until: e.target.value})}
                className={style.modalInput}
                min={editCoupon.valid_from}
                required
              />
            </div>
            <div className={style.formGroup}>
              <label>
                <input
                  type="checkbox"
                  checked={editCoupon.is_active}
                  onChange={(e) => setEditCoupon({...editCoupon, is_active: e.target.checked})}
                />
                {' '}Active (uncheck to pause this coupon without changing its dates)
              </label>
            </div>
            <div className={style.modalButtons}>
              <button onClick={() => setShowAddModal(false)} className={style.cancelBtn}>
                Cancel
              </button>
              <button onClick={() => handleSave(false)} className={style.saveBtn} disabled={isLoading}>
                {isLoading ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && (
        <div className={style.modalOverlay}>
          <div className={style.modal}>
            <h2>Edit Coupon</h2>
            <div className={style.formGroup}>
              <label>Coupon Name:</label>
              <input
                type="text"
                value={editCoupon.coupon_name}
                onChange={(e) => setEditCoupon({...editCoupon, coupon_name: e.target.value})}
                className={style.modalInput}
                placeholder="Enter coupon name *"
                required
              />
            </div>
            <div className={style.formGroup}>
              <label>Coupon Type:</label>
              <select
                value={editCoupon.coupon_type === customCouponType ? "custom" : editCoupon.coupon_type}
                onChange={(e) => handleCouponTypeChange(e.target.value)}
                className={style.modalInput}
                required
              >
                <option value="percentage">Percentage Discount</option>
                <option value="fixed">Fixed Amount Discount</option>
                <option value="buy_x_get_y">Buy X Get Y</option>
                <option value="free_shipping">Free Shipping</option>
                <option value="cashback">Cashback</option>
                <option value="loyalty">Loyalty Points</option>
                <option value="seasonal">Seasonal</option>
                <option value="first_time">First Time Customer</option>
                <option value="bulk">Bulk Purchase</option>
                <option value="referral">Referral Bonus</option>
                <option value="custom">Custom</option>
              </select>
              {showCustomInput && (
                <input
                  type="text"
                  value={customCouponType}
                  onChange={(e) => handleCustomCouponTypeChange(e.target.value)}
                  placeholder="Enter custom coupon type"
                  className={style.modalInput}
                  style={{ marginTop: '5px' }}
                />
              )}
            </div>
            <div className={style.formGroup}>
              <label>Coupon Value:</label>
              <input
                type="number"
                value={editCoupon.coupon_value}
                onChange={(e) => setEditCoupon({...editCoupon, coupon_value: e.target.value})}
                className={style.modalInput}
                placeholder={
                  editCoupon.coupon_type === "percentage" ? "Enter percentage (0-100) *" :
                  editCoupon.coupon_type === "fixed" ? "Enter amount in dollars *" :
                  editCoupon.coupon_type === "buy_x_get_y" ? "Enter quantity to buy *" :
                  editCoupon.coupon_type === "free_shipping" ? "Enter minimum order amount *" :
                  editCoupon.coupon_type === "cashback" ? "Enter cashback amount *" :
                  editCoupon.coupon_type === "loyalty" ? "Enter points value *" :
                  editCoupon.coupon_type === "seasonal" ? "Enter discount percentage *" :
                  editCoupon.coupon_type === "first_time" ? "Enter discount percentage *" :
                  editCoupon.coupon_type === "bulk" ? "Enter discount percentage *" :
                  editCoupon.coupon_type === "referral" ? "Enter bonus amount *" :
                  editCoupon.coupon_type === "custom" ? "Enter custom value *" :
                  "Enter coupon value *"
                }
                min="0"
                step={editCoupon.coupon_type === "percentage" || editCoupon.coupon_type === "seasonal" || editCoupon.coupon_type === "first_time" || editCoupon.coupon_type === "bulk" ? "1" : "0.01"}
                max={editCoupon.coupon_type === "percentage" ? "100" : undefined}
                required
              />
            </div>
            <div className={style.formGroup}>
              <label>Valid From:</label>
              <input
                type="date"
                value={editCoupon.valid_from}
                onChange={(e) => setEditCoupon({...editCoupon, valid_from: e.target.value})}
                className={style.modalInput}
                min={new Date().toISOString().split('T')[0]}
                required
              />
            </div>
            <div className={style.formGroup}>
              <label>Valid Until:</label>
              <input
                type="date"
                value={editCoupon.valid_until}
                onChange={(e) => setEditCoupon({...editCoupon, valid_until: e.target.value})}
                className={style.modalInput}
                min={editCoupon.valid_from}
                required
              />
            </div>
            <div className={style.formGroup}>
              <label>
                <input
                  type="checkbox"
                  checked={editCoupon.is_active}
                  onChange={(e) => setEditCoupon({...editCoupon, is_active: e.target.checked})}
                />
                {' '}Active (uncheck to pause this coupon without changing its dates)
              </label>
            </div>
            <div className={style.modalButtons}>
              <button onClick={() => setShowEditModal(false)} className={style.cancelBtn}>
                Cancel
              </button>
              <button onClick={() => handleSave(true)} className={style.saveBtn} disabled={isLoading}>
                {isLoading ? 'Updating...' : 'Update'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className={style.modalOverlay}>
          <div className={style.modal}>
            <h2>Confirm Delete</h2>
            <p>Are you sure you want to delete the selected coupon(s)?</p>
            <div className={style.modalButtons}>
              <button onClick={() => setShowDeleteConfirm(false)} className={style.cancelBtn}>
                Cancel
              </button>
              <button onClick={confirmDelete} className={style.deleteBtn} disabled={isLoading}>
                {isLoading ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CouponsPage;